import { useEffect, useRef, useState } from "react";
import { Save, Upload, Plus, Minus, RefreshCw, PauseCircle, PlayCircle } from "lucide-react";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { useSettings } from "@/contexts/SettingsContext";
import { toast } from "sonner";

const FEATURE_FLAGS = [
  { key: "show_likes", label: "Likes", help: "Show like button on posts" },
  { key: "show_comments", label: "Comments", help: "Show comment section on posts" },
  { key: "show_share", label: "Share", help: "Show native share button" },
  { key: "show_repost", label: "Repost", help: "Show repost-to-social menu" },
  { key: "show_save", label: "Save / Bookmark", help: "Allow visitors to save posts" },
  { key: "show_whatsapp_button", label: "WhatsApp Buttons", help: "Show WhatsApp on profile + posts" },
  { key: "show_contact_button", label: "Contact Button", help: "Show Contact button on profile" },
  { key: "show_floating_whatsapp", label: "Floating WhatsApp", help: "Show the floating green button" },
  { key: "show_reels_tab", label: "Reels Tab", help: "Show Reels tab on homepage" },
  { key: "show_saved_tab", label: "Saved Tab", help: "Show Saved tab on homepage" },
];

const THEMES = [
  { v: "dark", l: "Dark (premium)" },
  { v: "light", l: "Light" },
];
const GRID_STYLES = [
  { v: "classic", l: "Classic 3-column grid" },
  { v: "masonry", l: "Mixed mega-grid (mega-tiles span)" },
];

function Toggle({ value, onChange, "data-testid": tid }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      data-testid={tid}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${value ? "bg-blue-600" : "bg-slate-300"}`}
      aria-pressed={value}
    >
      <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${value ? "translate-x-5" : "translate-x-0.5"}`} />
    </button>
  );
}

export default function AdminSettings() {
  const { refresh } = useSettings();
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [uploadingKey, setUploadingKey] = useState(null);
  const [visits, setVisits] = useState({ base: 0, real: 0, total: 0 });
  const [adjustValue, setAdjustValue] = useState(1);
  const profileRef = useRef();
  const logoRef = useRef();

  useEffect(() => { api.get("/settings").then((r) => setForm(r.data)); }, []);
  useEffect(() => {
    api.get("/visits").then((r) => setVisits(r.data)).catch(() => {});
  }, []);

  const refreshVisits = async () => {
    try { const { data } = await api.get("/visits"); setVisits(data); } catch { /* ignore */ }
  };

  const adjustVisits = async (delta) => {
    try {
      const { data } = await api.post("/admin/visits/adjust", { delta });
      setVisits(data);
      toast.success(`Counter ${delta > 0 ? "+" : ""}${delta}`);
    } catch { toast.error("Adjust failed"); }
  };
  const setVisitsAbsolute = async () => {
    const v = window.prompt("Set REAL visit count (whole number, ≥ 0):");
    if (v === null) return;
    const n = parseInt(v, 10);
    if (isNaN(n) || n < 0) { toast.error("Invalid number"); return; }
    try {
      const { data } = await api.post("/admin/visits/adjust", { set: n });
      setVisits(data);
      toast.success("Counter set");
    } catch { toast.error("Set failed"); }
  };
  const resetVisits = async () => {
    if (!window.confirm("Reset real visits to 0? Base counter is preserved.")) return;
    try {
      const { data } = await api.post("/admin/visits/reset");
      setVisits(data);
      toast.success("Counter reset");
    } catch { toast.error("Reset failed"); }
  };

  const togglePause = async () => {
    const next = !(form.visit_growth_paused);
    try {
      await api.patch("/settings", { visit_growth_paused: next });
      setForm((s) => ({ ...s, visit_growth_paused: next }));
      toast.success(next ? "Auto-growth paused" : "Auto-growth resumed");
      refresh();
    } catch { toast.error("Update failed"); }
  };

  const save = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.patch("/settings", form);
      toast.success("Settings saved");
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally { setSubmitting(false); }
  };

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const uploadImage = async (key, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploadingKey(key);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set(key, data.url);
      toast.success(`${key === "profile_image" ? "Profile image" : "Logo"} uploaded`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setUploadingKey(null); }
  };

  return (
    <div data-testid="admin-settings-page">
      <h1 className="font-heading text-3xl font-medium text-slate-950 mb-2">Settings</h1>
      <p className="text-slate-500 mb-6">Control everything visitors see — profile, branding, social, theme, features, analytics.</p>

      <form onSubmit={save} className="space-y-6 max-w-3xl">
        {/* Profile + Logo */}
        <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4">
          <h2 className="font-heading text-lg font-medium text-slate-950">Profile & Logo</h2>
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Profile image (circular)</div>
              <div className="w-32 h-32 rounded-full overflow-hidden bg-slate-100 border border-slate-200 mb-3">
                {form.profile_image ? (
                  <img src={fileUrl(form.profile_image)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-xs text-slate-400">No image</div>
                )}
              </div>
              <input ref={profileRef} type="file" accept="image/*" onChange={(e) => uploadImage("profile_image", e.target.files?.[0])} className="hidden" />
              <div className="flex gap-2">
                <button type="button" onClick={() => profileRef.current?.click()} disabled={uploadingKey === "profile_image"} data-testid="upload-profile-image-btn" className="inline-flex items-center gap-1.5 border border-slate-200 hover:bg-slate-50 px-3 py-2 rounded-md text-xs">
                  <Upload className="w-3.5 h-3.5" /> {uploadingKey === "profile_image" ? "Uploading…" : "Upload / Replace"}
                </button>
                {form.profile_image && (
                  <button type="button" onClick={() => set("profile_image", "")} className="text-xs text-slate-500 hover:text-red-600 px-2">Remove</button>
                )}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Brand logo (replaces text)</div>
              <div className="h-24 bg-slate-50 border border-slate-200 rounded-md flex items-center justify-center px-4 mb-3 overflow-hidden">
                {form.logo_url ? (
                  <img src={form.logo_url.startsWith("http") || form.logo_url.startsWith("/") ? form.logo_url : fileUrl(form.logo_url)} alt="" className="max-h-20 w-auto object-contain" />
                ) : (
                  <div className="text-xs text-slate-400">No logo</div>
                )}
              </div>
              <input ref={logoRef} type="file" accept="image/*" onChange={(e) => uploadImage("logo_url", e.target.files?.[0])} className="hidden" />
              <div className="flex gap-2">
                <button type="button" onClick={() => logoRef.current?.click()} disabled={uploadingKey === "logo_url"} data-testid="upload-logo-btn" className="inline-flex items-center gap-1.5 border border-slate-200 hover:bg-slate-50 px-3 py-2 rounded-md text-xs">
                  <Upload className="w-3.5 h-3.5" /> {uploadingKey === "logo_url" ? "Uploading…" : "Upload / Replace"}
                </button>
                <input value={form.logo_url || ""} onChange={(e) => set("logo_url", e.target.value)} placeholder="/builddirect-logo.png or URL" className="flex-1 px-3 py-2 rounded-md border border-slate-200 text-xs font-mono" />
              </div>
            </div>
          </div>
        </div>

        {/* Branding */}
        <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4">
          <h2 className="font-heading text-lg font-medium text-slate-950">Branding & Bio</h2>
          <Field label="Brand name (used as alt-text & SEO)">
            <input value={form.brand_name || ""} onChange={(e) => set("brand_name", e.target.value)} data-testid="set-brand" className="w-full px-4 py-2.5 rounded-md border border-slate-200 text-sm" />
          </Field>
          <Field label="Tagline (Design • Build • Remodel • Renovate • Finish • Furnish)">
            <input value={form.tagline || ""} onChange={(e) => set("tagline", e.target.value)} data-testid="set-tagline" className="w-full px-4 py-2.5 rounded-md border border-slate-200 text-sm" />
          </Field>
          <Field label="Bio">
            <textarea rows={2} value={form.bio || ""} onChange={(e) => set("bio", e.target.value)} data-testid="set-bio" className="w-full px-4 py-2.5 rounded-md border border-slate-200 text-sm" />
          </Field>
          <Field label="Secondary services line">
            <textarea rows={2} value={form.secondary_services || ""} onChange={(e) => set("secondary_services", e.target.value)} data-testid="set-secondary-services" className="w-full px-4 py-2.5 rounded-md border border-slate-200 text-sm" />
          </Field>
        </div>

        {/* Location */}
        <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4">
          <h2 className="font-heading text-lg font-medium text-slate-950">Location</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="City / Country (e.g. Nigeria)">
              <input value={form.location_city || ""} onChange={(e) => set("location_city", e.target.value)} data-testid="set-location-city" className="w-full px-4 py-2.5 rounded-md border border-slate-200 text-sm" />
            </Field>
            <Field label="Region (e.g. Africa)">
              <input value={form.location_region || ""} onChange={(e) => set("location_region", e.target.value)} data-testid="set-location-region" className="w-full px-4 py-2.5 rounded-md border border-slate-200 text-sm" />
            </Field>
          </div>
        </div>

        {/* Theme & Grid */}
        <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4">
          <h2 className="font-heading text-lg font-medium text-slate-950">Appearance</h2>
          <Field label="Theme">
            <select value={form.theme || "dark"} onChange={(e) => set("theme", e.target.value)} data-testid="set-theme" className="w-full px-4 py-2.5 rounded-md border border-slate-200 text-sm bg-white">
              {THEMES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select>
          </Field>
          <Field label="Grid style">
            <select value={form.grid_style || "classic"} onChange={(e) => set("grid_style", e.target.value)} data-testid="set-grid-style" className="w-full px-4 py-2.5 rounded-md border border-slate-200 text-sm bg-white">
              {GRID_STYLES.map((g) => <option key={g.v} value={g.v}>{g.l}</option>)}
            </select>
          </Field>
          <Field label="Page size (infinite scroll)">
            <input type="number" min="6" max="60" value={form.page_size || 24} onChange={(e) => set("page_size", parseInt(e.target.value || "24"))} className="w-full px-4 py-2.5 rounded-md border border-slate-200 text-sm" />
          </Field>
          <Field label="Where do new posts land on the grid?">
            <select
              value={form.new_post_position || "top"}
              onChange={(e) => set("new_post_position", e.target.value)}
              data-testid="set-new-post-position"
              className="w-full px-4 py-2.5 rounded-md border border-slate-200 text-sm bg-white"
            >
              <option value="top">Top of the grid (newest first — Instagram style)</option>
              <option value="bottom">Bottom of the grid (curated portfolio — top stays fixed)</option>
            </select>
            <div className="text-[11px] text-slate-500 mt-1.5">
              "Top" means each new post appears at position 1 and pushes older posts down. "Bottom" keeps your hero posts at the top permanently — new uploads fill in from the bottom.
            </div>
          </Field>
        </div>

        {/* Video Quality */}
        <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4" data-testid="video-quality-panel">
          <div>
            <h2 className="font-heading text-lg font-medium text-slate-950">Video Quality</h2>
            <p className="text-xs text-slate-500 mt-0.5">Applied to all <strong>future</strong> uploads. Use the button below to re-process existing videos at the new setting.</p>
          </div>
          <Field label="Transcoding preset">
            <select
              value={form.video_quality || "standard"}
              onChange={(e) => set("video_quality", e.target.value)}
              data-testid="set-video-quality"
              className="w-full px-4 py-2.5 rounded-md border border-slate-200 text-sm bg-white"
            >
              <option value="standard">Standard — 1080p · CRF 23 · ~6 Mbps · FAST publish (recommended)</option>
              <option value="high">High Quality — 1080p · CRF 20 · ~10 Mbps (premium, slower)</option>
              <option value="original">Original Quality — up to 4K · CRF 18 · ~20 Mbps (slowest, near-lossless)</option>
            </select>
          </Field>
          <button
            type="button"
            data-testid="reprocess-all-videos"
            onClick={async () => {
              if (!window.confirm("Re-process every uploaded video at the current quality? This will run in the background and may take several minutes.")) return;
              try {
                const { data } = await api.post("/admin/videos/reprocess-all");
                toast.success(`Queued ${data.queued} video(s) for re-processing`);
              } catch (err) {
                toast.error(formatApiErrorDetail(err.response?.data?.detail));
              }
            }}
            className="inline-flex items-center gap-2 border border-slate-300 hover:bg-slate-50 px-4 py-2 rounded-md text-sm"
          >
            <RefreshCw className="w-4 h-4" />
            Re-process all existing videos at current quality
          </button>
        </div>

        {/* Site Visits */}
        <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-5" data-testid="visits-control-panel">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="font-heading text-lg font-medium text-slate-950">Site Visits Counter</h2>
              <p className="text-xs text-slate-500 mt-0.5">Displayed = base + real visits. Adjust manually below.</p>
            </div>
            <button
              type="button"
              onClick={refreshVisits}
              data-testid="visits-refresh"
              className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-900 px-2 py-1.5 rounded hover:bg-slate-100"
              title="Refresh"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
          </div>

          {/* Live snapshot */}
          <div className="grid grid-cols-3 gap-3">
            <Stat tid="visits-base-display" label="Base" value={visits.base?.toLocaleString() ?? "0"} />
            <Stat tid="visits-real-display" label="Real" value={visits.real?.toLocaleString() ?? "0"} />
            <Stat tid="visits-total-display" label="Total shown" value={visits.total?.toLocaleString() ?? "0"} highlight />
          </div>

          {/* Manual controls */}
          <div className="space-y-3 pt-1">
            <div className="text-xs uppercase tracking-widest text-slate-500">Manual adjustment</div>
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" onClick={() => adjustVisits(-adjustValue)} data-testid="visits-decrease" className="inline-flex items-center gap-1 border border-slate-200 hover:bg-slate-50 px-3 py-2 rounded-md text-sm">
                <Minus className="w-3.5 h-3.5" /> {adjustValue}
              </button>
              <input
                type="number"
                value={adjustValue}
                min="1"
                onChange={(e) => setAdjustValue(Math.max(1, parseInt(e.target.value || "1", 10)))}
                data-testid="visits-delta-input"
                className="w-24 px-3 py-2 rounded-md border border-slate-200 text-sm font-mono text-center"
              />
              <button type="button" onClick={() => adjustVisits(adjustValue)} data-testid="visits-increase" className="inline-flex items-center gap-1 bg-slate-950 hover:bg-slate-800 text-white px-3 py-2 rounded-md text-sm">
                <Plus className="w-3.5 h-3.5" /> {adjustValue}
              </button>
              <button type="button" onClick={setVisitsAbsolute} data-testid="visits-set" className="inline-flex items-center gap-1 border border-slate-200 hover:bg-slate-50 px-3 py-2 rounded-md text-sm">
                Set value…
              </button>
              <button type="button" onClick={resetVisits} data-testid="visits-reset" className="inline-flex items-center gap-1 border border-rose-200 text-rose-600 hover:bg-rose-50 px-3 py-2 rounded-md text-sm">
                <RefreshCw className="w-3.5 h-3.5" /> Reset to 0
              </button>
            </div>
          </div>

          {/* Base & growth */}
          <div className="grid sm:grid-cols-2 gap-4 pt-2 border-t border-slate-100">
            <Field label="Base counter (added to real)">
              <input type="number" min="0" value={form.visit_base ?? 120000} onChange={(e) => set("visit_base", parseInt(e.target.value || "0"))} data-testid="set-visit-base" className="w-full px-4 py-2.5 rounded-md border border-slate-200 text-sm font-mono" />
            </Field>
            <Field label="Auto-growth speed (seconds between drift)">
              <input type="number" min="2" max="600" value={form.visit_growth_seconds ?? 7} onChange={(e) => set("visit_growth_seconds", parseInt(e.target.value || "7"))} data-testid="set-visit-growth-seconds" className="w-full px-4 py-2.5 rounded-md border border-slate-200 text-sm font-mono" />
            </Field>
          </div>
          <div className="flex items-center gap-3 pt-1">
            <button
              type="button"
              onClick={togglePause}
              data-testid="visits-pause-toggle"
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium border ${
                form.visit_growth_paused ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50" : "border-amber-300 text-amber-700 hover:bg-amber-50"
              }`}
            >
              {form.visit_growth_paused ? (<><PlayCircle className="w-4 h-4" /> Resume auto-growth</>) : (<><PauseCircle className="w-4 h-4" /> Pause auto-growth</>)}
            </button>
            <span className="text-xs text-slate-500">
              Status: <span className={form.visit_growth_paused ? "text-amber-700 font-medium" : "text-emerald-700 font-medium"}>{form.visit_growth_paused ? "Paused" : "Active"}</span>
            </span>
          </div>
          <p className="text-xs text-slate-500">Tip: lower seconds = faster growth. Save settings to apply growth speed.</p>
        </div>

        {/* Feature Flags */}
        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <h2 className="font-heading text-lg font-medium text-slate-950 mb-1">Features</h2>
          <p className="text-xs text-slate-500 mb-4">Toggle visitor-facing features on or off instantly.</p>
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3" data-testid="feature-flags">
            {FEATURE_FLAGS.map((f) => (
              <div key={f.key} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-950">{f.label}</div>
                  <div className="text-xs text-slate-500">{f.help}</div>
                </div>
                <Toggle value={form[f.key] !== false} onChange={(v) => set(f.key, v)} data-testid={`flag-${f.key}`} />
              </div>
            ))}
          </div>
        </div>

        {/* Contact & Social */}
        <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4">
          <h2 className="font-heading text-lg font-medium text-slate-950">Contact & Social</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="WhatsApp number (with country code)">
              <input value={form.whatsapp_number || ""} onChange={(e) => set("whatsapp_number", e.target.value)} placeholder="+254700000000" data-testid="set-whatsapp" className="w-full px-4 py-2.5 rounded-md border border-slate-200 text-sm" />
            </Field>
            <Field label="Mobile number (display)">
              <input value={form.mobile_number || ""} onChange={(e) => set("mobile_number", e.target.value)} placeholder="+234..." data-testid="set-mobile" className="w-full px-4 py-2.5 rounded-md border border-slate-200 text-sm" />
            </Field>
            <Field label="Contact email">
              <input value={form.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} data-testid="set-email" className="w-full px-4 py-2.5 rounded-md border border-slate-200 text-sm" />
            </Field>
            <Field label="Pinterest URL">
              <input value={form.pinterest || ""} onChange={(e) => set("pinterest", e.target.value)} placeholder="https://pinterest.com/…" data-testid="set-pinterest" className="w-full px-4 py-2.5 rounded-md border border-slate-200 text-sm" />
            </Field>
            <Field label="TikTok URL">
              <input value={form.tiktok || ""} onChange={(e) => set("tiktok", e.target.value)} placeholder="https://tiktok.com/@…" data-testid="set-tiktok" className="w-full px-4 py-2.5 rounded-md border border-slate-200 text-sm" />
            </Field>
            <Field label="Instagram URL">
              <input value={form.instagram || ""} onChange={(e) => set("instagram", e.target.value)} placeholder="https://instagram.com/…" data-testid="set-instagram" className="w-full px-4 py-2.5 rounded-md border border-slate-200 text-sm" />
            </Field>
            <Field label="Facebook URL">
              <input value={form.facebook || ""} onChange={(e) => set("facebook", e.target.value)} placeholder="https://facebook.com/…" data-testid="set-facebook" className="w-full px-4 py-2.5 rounded-md border border-slate-200 text-sm" />
            </Field>
          </div>
        </div>

        <button
          type="submit"
          disabled={submitting}
          data-testid="set-save-btn"
          className="inline-flex items-center gap-2 bg-slate-950 hover:bg-slate-800 text-white px-5 py-3 rounded-md text-sm font-medium disabled:opacity-50"
        >
          <Save className="w-4 h-4" /> {submitting ? "Saving…" : "Save settings"}
        </button>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block text-sm">
      <div className="text-xs uppercase tracking-widest text-slate-500 mb-1.5">{label}</div>
      {children}
    </label>
  );
}

function Stat({ label, value, tid, highlight }) {
  return (
    <div className={`text-center p-3 rounded-md border ${highlight ? "border-blue-200 bg-blue-50" : "border-slate-200 bg-slate-50"}`} data-testid={tid}>
      <div className={`font-mono text-base sm:text-lg ${highlight ? "text-blue-700 font-semibold" : "text-slate-900 font-medium"}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">{label}</div>
    </div>
  );
}
